import type { OwnershipModel, Relation, RelationKind } from '../model';

export interface Forest {
  primaryRelationOf: ReadonlyMap<number, Relation>;
  childrenOf: ReadonlyMap<number, number[]>;
  roots: number[];
  trayIds: number[];
  incomingOf: ReadonlyMap<number, Relation[]>;
  secondaryRelations: Relation[];
  loopRelations: Relation[];
}

class ForestHelper {
  static readonly VIRTUAL_ROOT = -1;

  static readonly KIND_RANK: Readonly<Record<RelationKind, number>> = {
    equity: 0,
    generalPartner: 1,
    limitedPartner: 2,
    control: 3,
  };

  static readonly COLLATOR = new Intl.Collator('en', {
    numeric: true,
    sensitivity: 'base',
  });

  static compareEntities(model: Readonly<OwnershipModel>) {
    return (a: number, b: number): number => {
      const byName = ForestHelper.COLLATOR.compare(
        model.entitiesById.get(a)?.name ?? '',
        model.entitiesById.get(b)?.name ?? ''
      );
      return byName || a - b;
    };
  }

  // Detached entities are owned but placed elsewhere (a shared band), so they are nobody's child and no root.
  static build(
    model: Readonly<OwnershipModel>,
    loopIds: ReadonlySet<string>,
    detached: ReadonlySet<number> = new Set()
  ): Forest {
    const byName = ForestHelper.compareEntities(model);
    const incomingOf = new Map<number, Relation[]>();
    const related = new Set<number>();
    const loopRelations: Relation[] = [];
    model.relations.forEach((relation) => {
      related.add(relation.ownerId);
      related.add(relation.ownedId);
      if (loopIds.has(relation.id)) {
        loopRelations.push(relation);
        return;
      }
      incomingOf.set(relation.ownedId, [
        ...(incomingOf.get(relation.ownedId) ?? []),
        relation,
      ]);
    });
    incomingOf.forEach((list) =>
      list.sort(
        (a, b) =>
          b.percent - a.percent ||
          ForestHelper.KIND_RANK[a.kind] - ForestHelper.KIND_RANK[b.kind] ||
          byName(a.ownerId, b.ownerId)
      )
    );

    const primaryRelationOf = new Map<number, Relation>();
    const childrenOf = new Map<number, number[]>();
    const secondaryRelations: Relation[] = [];
    incomingOf.forEach(([primary, ...rest], ownedId) => {
      primaryRelationOf.set(ownedId, primary);
      secondaryRelations.push(...rest);
      if (!detached.has(ownedId))
        childrenOf.set(primary.ownerId, [
          ...(childrenOf.get(primary.ownerId) ?? []),
          ownedId,
        ]);
    });
    childrenOf.forEach((list) => list.sort(byName));

    const subtreeSize = ForestHelper.getSubtreeSizes(childrenOf);
    const roots = model.entities
      .map((entity) => entity.id)
      .filter((id) => related.has(id) && !primaryRelationOf.has(id))
      .sort(
        (a, b) =>
          (subtreeSize.get(b) ?? 1) - (subtreeSize.get(a) ?? 1) || byName(a, b)
      );
    const trayIds = model.entities
      .map((entity) => entity.id)
      .filter((id) => !related.has(id))
      .sort(byName);

    return {
      primaryRelationOf,
      childrenOf,
      roots,
      trayIds,
      incomingOf,
      secondaryRelations: secondaryRelations.sort(
        (a, b) => a.ownedId - b.ownedId || a.ownerId - b.ownerId
      ),
      loopRelations,
    };
  }

  static getChildren(forest: Readonly<Forest>, entityId: number): number[] {
    return forest.childrenOf.get(entityId) ?? [];
  }

  static isLeaf(forest: Readonly<Forest>, entityId: number): boolean {
    return ForestHelper.getChildren(forest, entityId).length === 0;
  }

  private static getSubtreeSizes(
    childrenOf: ReadonlyMap<number, number[]>
  ): Map<number, number> {
    const sizes = new Map<number, number>();
    const sizeOf = (start: number): number => {
      const order: number[] = [];
      const stack = [start];
      while (stack.length > 0) {
        const id = stack.pop();
        if (id !== undefined && !sizes.has(id)) {
          order.push(id);
          stack.push(...(childrenOf.get(id) ?? []));
        }
      }
      order.reverse().forEach((id) => {
        sizes.set(
          id,
          1 +
            (childrenOf.get(id) ?? []).reduce(
              (sum, child) => sum + (sizes.get(child) ?? 1),
              0
            )
        );
      });
      return sizes.get(start) ?? 1;
    };
    childrenOf.forEach((_children, id) => sizeOf(id));
    return sizes;
  }
}

export { ForestHelper };
